import type { UserValue } from '@atlassian/jira-issue-field-assignee/src/common/types.tsx';
import useAssigneeField from '@atlassian/jira-issue-field-assignee/src/services/use-assignee-field/index.tsx';
import { useUserCustomField } from '@atlassian/jira-issue-field-base-user-picker/src/services/use-user-picker-field/index.tsx';
import type { UserValue as UserCustomFieldValue } from '@atlassian/jira-issue-field-base-user-picker/src/services/use-user-picker-field/types.tsx';
import { useMultiUserCustomField } from '@atlassian/jira-issue-field-multi-user-picker-editview-full/src/services/use-multi-user-picker-field/index.tsx';
import type { User as MultiUserCustomFieldValue } from '@atlassian/jira-issue-field-multi-user-picker-editview-full/src/services/use-multi-user-picker-field/types.tsx';
import { useReporterField } from '@atlassian/jira-issue-field-reporter/src/services/use-reporter-field/index.tsx';
import type { ReporterValue } from '@atlassian/jira-issue-field-reporter/src/services/use-reporter-field/types.tsx';
import {
	USER_CF_TYPE,
	REPORTER_TYPE,
	ASSIGNEE_TYPE,
	MULTI_USER_CF_TYPE,
	PEOPLE_CF_TYPE,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import type { AvatarUrls } from '@atlassian/jira-shared-types/src/rest/jira/user.tsx';
import { createClipboardHelperComponent } from '../../../clipboard-handler/create-clipboard-helper-component/index.tsx';
import { useClipboardHandler } from '../../../clipboard-handler/use-clipboard-handler/index.tsx';
import {
	assertClipboardDataIsCompatible,
	assertClipboardHasRichFormat,
} from '../../../clipboard-handler/utils.tsx';
import type {
	ClipboardItem,
	ListIssueFieldTransformed,
	ListIssueForCopyPaste,
	PasteHandler,
	PasteMetadata,
} from '../../../types.tsx';
import { multiUserSupportedTypes, userSupportedTypes } from '../supported-field-types/index.tsx';
import type { TransformedUser } from '../types.tsx';

type LocalUserValue = {
	accountId: string;
	avatarUrls: Readonly<Partial<AvatarUrls>>;
	avatarUrl: string;
	displayName: string;
};

const isTransformedUser = (fieldValue: unknown): fieldValue is TransformedUser =>
	fieldValue != null &&
	typeof fieldValue === 'object' &&
	'accountId' in fieldValue &&
	'name' in fieldValue &&
	'picture' in fieldValue;

const isTransformedUserList = (fieldValue: unknown): fieldValue is TransformedUser[] =>
	fieldValue != null &&
	Array.isArray(fieldValue) &&
	fieldValue.length > 0 &&
	fieldValue.every(isTransformedUser);

function isUserValue<T>(fieldValue: unknown): fieldValue is T {
	return (
		fieldValue != null &&
		typeof fieldValue === 'object' &&
		'accountId' in fieldValue &&
		'displayName' in fieldValue &&
		'avatarUrls' in fieldValue
	);
}

function isUserValueList<T>(fieldValue: unknown): fieldValue is T[] {
	return (
		fieldValue != null &&
		Array.isArray(fieldValue) &&
		fieldValue.length > 0 &&
		fieldValue.every(isUserValue)
	);
}

type UserTypesPermitted = LocalUserValue | UserValue | ReporterValue;

function transformToUserValue<T>(fieldValue: TransformedUser): T {
	const { accountId, name, picture } = fieldValue;

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	return {
		accountId,
		displayName: name,
		avatarUrl: picture,
		avatarUrls: {
			'48x48': picture,
		},
	} as T;
}

function getPastedValue<T = UserTypesPermitted>(
	clipboardItem: ClipboardItem,
	metadata: PasteMetadata | null,
): T | null {
	if (
		!assertClipboardHasRichFormat(clipboardItem) ||
		!assertClipboardDataIsCompatible(metadata, userSupportedTypes)
	) {
		return null;
	}

	if (!isUserValue<T>(metadata.fieldValue)) {
		return null;
	}

	return metadata.fieldValue;
}

function getPastedValuePlural<T = UserTypesPermitted>(
	clipboardItem: ClipboardItem,
	metadata: PasteMetadata | null,
): T[] | null {
	if (
		!assertClipboardHasRichFormat(clipboardItem) ||
		!assertClipboardDataIsCompatible(metadata, multiUserSupportedTypes)
	) {
		return null;
	}

	if (!isUserValueList<T>(metadata.fieldValue)) {
		return null;
	}

	return metadata.fieldValue;
}

const useAssigneeClipboardHandler: PasteHandler = (
	issue: ListIssueForCopyPaste,
	field: ListIssueFieldTransformed,
) => {
	const [{ value }, { saveValue }] = useAssigneeField({
		issueKey: issue.key,
		initialValue: isTransformedUser(field.value)
			? transformToUserValue<UserValue>(field.value)
			: undefined,
	});

	return useClipboardHandler<UserValue>({
		issue,
		field,
		currentStringValue: value?.displayName || null,
		currentValue: value,
		getPastedValue: getPastedValue<UserValue>,
		saveValue,
	});
};

const useReporterClipboardHandler: PasteHandler = (
	issue: ListIssueForCopyPaste,
	field: ListIssueFieldTransformed,
) => {
	const [{ value }, { saveValue }] = useReporterField({
		issueKey: issue.key,
		initialValue: isTransformedUser(field.value)
			? transformToUserValue<ReporterValue>(field.value)
			: undefined,
	});

	return useClipboardHandler<ReporterValue>({
		issue,
		field,
		currentStringValue: value?.displayName || null,
		currentValue: value,
		getPastedValue: getPastedValue<ReporterValue>,
		saveValue,
	});
};

const useSingleUserClipboardHandler: PasteHandler = (
	issue: ListIssueForCopyPaste,
	field: ListIssueFieldTransformed,
) => {
	const [{ value }, { saveValue }] = useUserCustomField({
		issueKey: issue.key,
		fieldKey: field.fieldId,
		initialValue: isTransformedUser(field.value)
			? transformToUserValue<UserCustomFieldValue>(field.value)
			: undefined,
	});

	return useClipboardHandler<UserCustomFieldValue>({
		issue,
		field,
		currentStringValue: value?.displayName || null,
		currentValue: value,
		getPastedValue: getPastedValue<UserCustomFieldValue>,
		saveValue,
	});
};

const useMultiUserClipboardHandler: PasteHandler = (
	issue: ListIssueForCopyPaste,
	field: ListIssueFieldTransformed,
) => {
	const [{ value }, { saveValue }] = useMultiUserCustomField({
		issueKey: issue.key,
		fieldKey: field.fieldId,
		initialValue: isTransformedUserList(field.value)
			? field.value.map(transformToUserValue<MultiUserCustomFieldValue>)
			: undefined,
	});

	return useClipboardHandler<MultiUserCustomFieldValue[]>({
		issue,
		field,
		currentStringValue: value?.map((user) => user.displayName).join(', ') || null,
		currentValue: value,
		getPastedValue: getPastedValuePlural<MultiUserCustomFieldValue>,
		saveValue,
	});
};

const UserClipboardHelper = createClipboardHelperComponent(useSingleUserClipboardHandler);
const MultiUserClipboardHelper = createClipboardHelperComponent(useMultiUserClipboardHandler);
const AssigneeClipboardHelper = createClipboardHelperComponent(useAssigneeClipboardHandler);
const ReporterClipboardHelper = createClipboardHelperComponent(useReporterClipboardHandler);

export const userClipboardHandler = {
	[USER_CF_TYPE]: UserClipboardHelper,
	[REPORTER_TYPE]: ReporterClipboardHelper,
	[ASSIGNEE_TYPE]: AssigneeClipboardHelper,
};

export const multiUserClipboardHandler = {
	[MULTI_USER_CF_TYPE]: MultiUserClipboardHelper,
	[PEOPLE_CF_TYPE]: MultiUserClipboardHelper,
};
